import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { Photo } from "./Photo";
import { GalleryItem } from "./GalleryItem";
import { PHOTOS } from "../images";

const ITEMS = [
  { src: PHOTOS.gallery[0], label: "The villa exterior", tone: "brunswick", aspect: "aspect-[4/3]", alt: "The exterior of Dune Villa 1 and Dune Villa 2 at Area 83, in warm evening light" },
  { src: PHOTOS.gallery[1], label: "The courtyard pool", tone: "moss", aspect: "aspect-[3/4]", alt: "The private courtyard pool with a thatched umbrella at a Dune Villa" },
  { src: PHOTOS.gallery[2], label: "The living space", tone: "champagne", aspect: "aspect-[4/3]", alt: "The sculptural living and dining space inside a Dune Villa" },
  { src: PHOTOS.gallery[3], label: "The games lounge", tone: "sepia", aspect: "aspect-[4/3]", alt: "The games lounge with a pool table and arched windows inside a Dune Villa" },
  { src: PHOTOS.gallery[4], label: "The lounge", tone: "sepia", aspect: "aspect-[4/3]", alt: "The lounge inside a Dune Villa, wrapped in palms and natural light" },
  { src: PHOTOS.gallery[5], label: "The stairwell", tone: "sepia", aspect: "aspect-[4/3]", alt: "Woven pendants hanging above the staircase of a Dune Villa" },
  { src: PHOTOS.gallery[6], label: "The bedrooms", tone: "sepia", aspect: "aspect-[3/4]", alt: "A bedroom with an arched window and woven pendant inside a Dune Villa" },
  { src: PHOTOS.gallery[7], label: "The kitchen", tone: "champagne", aspect: "aspect-[3/4]", alt: "The full kitchen and island inside a Dune Villa" },
  { src: PHOTOS.gallery[8], label: "The courts, from above", tone: "licorice", aspect: "aspect-[4/3]", alt: "Aerial view of the sports courts at Area 83" },
];

export const Gallery = () => {
  const [active, setActive] = useState(null);

  const move = (dir) => setActive((a) => (a + dir + ITEMS.length) % ITEMS.length);

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-white py-20 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-moss">Gallery</p>
          <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-tight text-brand-licorice sm:text-5xl">
            A closer look
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="columns-2 gap-4 md:columns-3 [&>*]:mb-4">
            {ITEMS.map((item, i) => (
              <GalleryItem key={item.label} item={item} index={i} onOpen={() => setActive(i)} />
            ))}
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-licorice/95 p-5"
            onClick={() => setActive(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
          >
            <button
              type="button"
              data-testid="lightbox-close"
              aria-label="Close image viewer"
              className="absolute right-5 top-5 text-brand-ivory/80 transition-opacity hover:opacity-60"
              onClick={() => setActive(null)}
            >
              <X size={30} strokeWidth={1.25} />
            </button>
            <button
              type="button"
              data-testid="lightbox-prev"
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 p-3 text-brand-ivory/80 transition-opacity hover:opacity-60 sm:left-8"
              onClick={(e) => { e.stopPropagation(); move(-1); }}
            >
              <ChevronLeft size={34} strokeWidth={1.25} />
            </button>
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[82vh] w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-h-[78vh] w-full overflow-hidden">
                {ITEMS[active].src ? (
                  <img src={ITEMS[active].src} alt={ITEMS[active].alt} className="mx-auto max-h-[78vh] w-auto max-w-full object-contain" />
                ) : (
                  <div className="aspect-[3/2] w-full">
                    <Photo src={null} alt={ITEMS[active].alt} tone={ITEMS[active].tone} />
                  </div>
                )}
              </div>
              <p data-testid="lightbox-caption" className="mt-4 text-center font-serif text-lg italic text-brand-ivory/80">
                {ITEMS[active].label}
              </p>
            </motion.div>
            <button
              type="button"
              data-testid="lightbox-next"
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-3 text-brand-ivory/80 transition-opacity hover:opacity-60 sm:right-8"
              onClick={(e) => { e.stopPropagation(); move(1); }}
            >
              <ChevronRight size={34} strokeWidth={1.25} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
